/**
 * Some host pages (e.g. Shopify storefronts, ad-tech scripts) replace the
 * browser-native IntersectionObserver with their own JS implementation that
 * can be incomplete or buggy. Native constructors always stringify to
 * `function IntersectionObserver() { [native code] }`, so checking for
 * `[native code]` reliably distinguishes browser-provided implementations
 * from JS shims. We use `Function.prototype.toString.call` instead of calling
 * `.toString()` directly to avoid overridden `toString` methods.
 */
function isNativeIntersectionObserver(): boolean {
  try {
    return Function.prototype.toString
      .call(window.IntersectionObserver)
      .includes('[native code]')
  } catch {
    return false
  }
}

/**
 * Returns the IntersectionObserver constructor. Uses the browser-native
 * implementation when available and trustworthy, otherwise falls back to a
 * lightweight local polyfill that does not touch `window.IntersectionObserver`.
 */
export const ensureIntersectionObserver = async (): Promise<
  typeof IntersectionObserver
> => {
  if (typeof window === 'undefined') {
    throw new Error('No IntersectionObserver in SSR')
  }

  if ('IntersectionObserver' in window && isNativeIntersectionObserver()) {
    return window.IntersectionObserver
  }

  const { IntersectionObserverPolyfill } =
    await import('./IntersectionObserverPolyfill')
  return IntersectionObserverPolyfill as unknown as typeof IntersectionObserver
}
